import {
  getFirestore,
  collection,
  addDoc,
  getDocs,
  where,
  query,
  setDoc,
  doc,
  getDoc,
  runTransaction,
} from "firebase/firestore";
import { app, functions } from "../firebase/app";
import { Id, makeWishlist, Wishlist, WishlistItem } from "../types";
import { httpsCallable } from "firebase/functions";

const db = getFirestore(app);
const storeKind = "wishlists";

export async function fetchWishlists(userId: Id): Promise<Wishlist[]> {
  const q = query(collection(db, storeKind), where("userId", "==", userId));
  try {
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => {
      return { ...doc.data(), id: doc.id } as Wishlist;
    });
  } catch (e) {
    console.error("fetchWishlist", e);
    return [];
  }
}

export async function fetchSingleWishlist(
  id: Id,
): Promise<Wishlist | undefined> {
  const docSnap = await getDoc(doc(db, storeKind, id));
  return docSnap.exists() ? ({ ...docSnap.data(), id } as Wishlist) : undefined;
}

export async function fetchMultipleWishlists(
  ids: Id[],
): Promise<Wishlist[] | undefined> {
  if (!ids || ids.length === 0) {
    return [];
  }
  const q = query(collection(db, storeKind), where("id", "in", ids));
  try {
    const snap = await getDocs(q);
    return snap.docs.map((doc) => {
      return { ...doc.data(), id: doc.id } as Wishlist;
    });
  } catch (e) {
    console.error("fetchWishlist", e);
    return [];
  }
}

async function createWishlist(userId: Id): Promise<Wishlist> {
  const wishlist = makeWishlist(userId);
  const response = await addDoc(collection(db, storeKind), wishlist);
  const result = { ...wishlist, id: response.id };
  return result;
}

export async function updateWishlist(wishlist: Wishlist): Promise<void> {
  return await setDoc(doc(db, storeKind, wishlist.id), wishlist);
}

let creatingPromise: Promise<Wishlist> | undefined = undefined;

export async function loadOrCreateWishlists(userId: Id): Promise<Wishlist[]> {
  if (creatingPromise) {
    const wishlist = await creatingPromise;
    creatingPromise = undefined;
    return [wishlist];
  }

  let wishlists = await fetchWishlists(userId);
  if (wishlists.length === 0) {
    if (!creatingPromise) {
      creatingPromise = createWishlist(userId);
    }

    try {
      const result = await creatingPromise;
      wishlists = [result];
    } finally {
      creatingPromise = undefined;
    }
  }

  return wishlists;
}

const remoteUpdateWish = httpsCallable(functions, "updateWish");

export async function updateWish(
  wishlistId: Id,
  wish: WishlistItem,
): Promise<Wishlist> {
  try {
    return (await remoteUpdateWish({ wishlistId, wish })).data as Wishlist;
  } catch (e) {
    console.error("updateWith", e);
    throw e;
  }
}

export async function deleteWish(
  wishlistId: Id,
  wishId: Id,
): Promise<Wishlist> {
  try {
    const sfDocRef = doc(db, storeKind, wishlistId);
    await runTransaction(db, async (transaction) => {
      const sfDoc = await transaction.get(sfDocRef);
      if (!sfDoc.exists()) {
        throw "Wishlist doesn't exist!";
      }

      const wishlist: Wishlist = {
        ...sfDoc.data(),
        id: wishlistId,
      } as Wishlist;
      const items = wishlist.items.filter((w) => w.id !== wishId);

      transaction.update(sfDocRef, { items });
    });

    return {
      ...(await getDoc(sfDocRef)).data(),
      id: wishlistId,
    } as Wishlist;
  } catch (e) {
    console.error("updateWith", e);
    throw e;
  }
}
